/* The behaviours every server-rendered page's chrome needs: the header summary
 * from /meta, the language switcher, the extension CTA and the API line in the
 * footer.
 *
 * Four pages want exactly this and nothing more — the landing, the privacy
 * policy, the live stream grid and the settings page, the last two alongside a
 * controller of their own. The catalog is the exception: app.js is 2000 lines
 * around a live list and owns its own copy of these, because it has to
 * re-render rows on a language switch.
 *
 * The switcher is not this module's own any more. Every public page exists once
 * per language, so a switch is a navigation, and the "where does this document
 * live in language X" question is answered the same way for the catalog's
 * switcher as for this one — out of the page's own `hreflang` links. That lives
 * in lang-nav.js and this file just calls it.
 */

(function (window, document) {
  'use strict';

  var RHF = window.RHF || (window.RHF = {});

  var config = RHF.config || {};

  /* A missing i18n runtime must not take a page down: the markup is already
     readable in the language it was authored in. Same stub idea as app.js. */
  var i18n = RHF.i18n || {
    lang: function () { return 'en'; },
    setLang: function () { return undefined; },
    onChange: function () { return undefined; }
  };

  var dom = {};
  /* Kept so a language switch can re-render the numbers: they are localised
     (thousands separators, "5 minutes ago"), the static markup is not. */
  var meta = null;

  function byId(id) {
    return document.getElementById(id);
  }

  function setText(node, value) {
    if (node) {
      node.textContent = value;
    }
  }

  function renderStats() {
    var fmt = RHF.format;
    if (!fmt) {
      return;
    }
    setText(dom.statServers, meta && meta.serversTotal !== null ? fmt.formatNumber(meta.serversTotal) : '—');
    setText(dom.statOnline, meta && meta.serversOnline !== null ? fmt.formatNumber(meta.serversOnline) : '—');
    setText(dom.statPlayers, meta && meta.playersTotal !== null ? fmt.formatNumber(meta.playersTotal) : '—');
    setText(dom.statSync, meta && meta.lastSyncAt ? fmt.formatPast(meta.lastSyncAt) : '—');
    if (dom.statSync && meta && meta.lastSyncAt) {
      dom.statSync.title = fmt.formatDateTime(meta.lastSyncAt);
    }
  }

  function loadMeta() {
    if (!RHF.api) {
      return;
    }
    RHF.api.getMeta().then(function (payload) {
      meta = payload;
      renderStats();
    }, function () {
      /* The dashes of the markup stay: these pages have nothing to retry with */
    });
  }

  /* The switch is a navigation to the same document in another language, and
     lang-nav.js is what knows where that is. Without the module the page is
     still readable, so the fallback is the repaint this used to do before the
     pages had URLs of their own. */
  function selectLanguage(code) {
    if (RHF.langNav) {
      RHF.langNav.selectLanguage(code);
      return;
    }
    i18n.setLang(code);
  }

  function bindLanguage() {
    if (dom.langSwitch) {
      dom.langSwitch.value = i18n.lang();
      dom.langSwitch.addEventListener('change', function () {
        selectLanguage(dom.langSwitch.value);
      });
    }
    i18n.onChange(function () {
      if (dom.langSwitch) {
        dom.langSwitch.value = i18n.lang();
      }
      renderStats();
    });
  }

  /**
   * The header CTA ships hidden and stays hidden until config.extensionUrl
   * carries the store listing. Unlike the catalog, these pages have no promo
   * band to jump to, so the button goes straight to the listing.
   */
  function setupExtensionLink() {
    if (!dom.ctaTop || !config.extensionUrl) {
      return;
    }
    dom.ctaTop.href = config.extensionUrl;
    dom.ctaTop.target = '_blank';
    dom.ctaTop.rel = 'noopener noreferrer';
    dom.ctaTop.hidden = false;
  }

  function boot() {
    dom = {
      statServers: byId('stat-servers'),
      statOnline: byId('stat-online'),
      statPlayers: byId('stat-players'),
      statSync: byId('stat-sync'),
      langSwitch: byId('lang-switch'),
      footerMeta: byId('footer-meta'),
      ctaTop: byId('cta-top')
    };

    setText(dom.footerMeta, 'API ' + (config.apiBase || '/api/v1'));
    setupExtensionLink();
    bindLanguage();
    loadMeta();
  }

  RHF.chrome = {
    boot: boot
  };

  /* The catalog opts out by not importing this file; every page that does
     import it wants the whole set. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})(window, document);
